/**
 * The global wait — ONE answer to "is anything happening?", for the whole app.
 *
 * ── Why it exists ───────────────────────────────────────────────────────────
 * The app had a dozen moments where the honest state was "not yet", and it said
 * so a dozen different ways or not at all. Opening the site rendered NOTHING
 * until the sign-in check came back (AuthGate returns null on purpose — see
 * that file). A redirect to /login was a blank page that became a different
 * blank page. The notebook opened empty and filled a moment later. Each of
 * those is a beat in which the page is indistinguishable from a broken one.
 *
 * So there is one layer, mounted once in the root layout (components/
 * GlobalWait.tsx), and this file is the only way to ask for it. Anything that
 * is waiting takes a HOLD; the layer is up while any hold is; and whoever took
 * the hold gives it back. Nobody draws their own spinner for a whole-page wait
 * again.
 *
 * ── The three ways to ask ───────────────────────────────────────────────────
 *   useWait(active, label)       a component is waiting on something it knows
 *                                about (a fetch, a sign-in check). The usual one.
 *   waitFor(promise, label)      one awaited call, start to finish.
 *   beginWait(label) → release   for code outside React (a hard navigation).
 *
 * Page-to-page navigation asks for itself: instrumentation-client.ts hears every
 * App Router transition start and takes a hold that the layer releases when the
 * pathname actually changes. No page has to opt in.
 *
 * ── Three promises the layer makes, enforced HERE ───────────────────────────
 * 1. It can never trap the page. Every hold expires on its own (HOLD_MAX_MS,
 *    shorter for navigations) whether or not its owner remembers to release it.
 *    A wait that outlives its cause is worse than the blank it replaced: that
 *    one could at least be clicked through.
 * 2. Holds are counted, not toggled. Two things waiting at once is the normal
 *    case (the sign-in check and the redirect it causes overlap by design), and
 *    a boolean would let the first to finish hide the second.
 * 3. It starts UP. The server renders the layer visible with the `boot` hold in
 *    place, so the very first paint of the site is the wait rather than a white
 *    page — and GlobalWait gives `boot` back as soon as it hydrates.
 *
 * ── Why the state lives on `window` ─────────────────────────────────────────
 * instrumentation-client.ts is bundled by the framework, not imported by a
 * page. If the bundler ever gives it its own copy of this module, a
 * module-level variable would be two variables and navigation holds would go
 * to a store nothing is listening to. One object on `window` cannot be two.
 */

import { useEffect, useSyncExternalStore } from 'react';

export type WaitHold = {
  id: number;
  /** What is happening, as a short phrase with no trailing dots: "Opening your notebook". */
  label: string;
  /** Show at once instead of after the usual grace period. Boot and hard navigations. */
  instant: boolean;
  kind: 'boot' | 'nav' | 'work';
};

export type WaitState = { holds: readonly WaitHold[] };

/** A hold nobody released is dropped after this long. See promise 1 above. */
const HOLD_MAX_MS = 25_000;
/** A navigation that has not landed by now is not going to: stop covering the page. */
const NAV_MAX_MS = 10_000;

const BOOT_ID = 1;

/**
 * The state every render starts from — on the server, and on the client's first
 * render, which must match it exactly or hydration throws the tree away.
 */
const BOOT_STATE: WaitState = {
  // The card already carries the brand name on the line above the label, so
  // this says what is happening rather than repeating where.
  holds: [{ id: BOOT_ID, label: 'Getting everything ready', instant: true, kind: 'boot' }],
};

type Store = {
  state: WaitState;
  listeners: Set<() => void>;
  nextId: number;
  timers: Map<number, ReturnType<typeof setTimeout>>;
};

declare global {
  interface Window { __gtWait?: Store }
}

function store(): Store | null {
  if (typeof window === 'undefined') return null;
  return (window.__gtWait ??= {
    state: BOOT_STATE,
    listeners: new Set(),
    nextId: BOOT_ID + 1,
    timers: new Map(),
  });
}

function set(s: Store, holds: readonly WaitHold[]): void {
  s.state = { holds };
  s.listeners.forEach((fn) => fn());
}

function release(id: number): void {
  const s = store();
  if (!s) return;
  const t = s.timers.get(id);
  if (t) { clearTimeout(t); s.timers.delete(id); }
  if (s.state.holds.some((h) => h.id === id)) set(s, s.state.holds.filter((h) => h.id !== id));
}

/**
 * Take a hold. Returns the function that gives it back; calling it twice is
 * harmless, and not calling it at all only costs HOLD_MAX_MS.
 */
export function beginWait(
  label: string,
  opts: { instant?: boolean; kind?: WaitHold['kind']; maxMs?: number } = {},
): () => void {
  const s = store();
  if (!s) return () => {};
  const id = s.nextId++;
  set(s, [...s.state.holds, { id, label, instant: !!opts.instant, kind: opts.kind ?? 'work' }]);
  s.timers.set(id, setTimeout(() => release(id), opts.maxMs ?? HOLD_MAX_MS));
  return () => release(id);
}

/** Hold for exactly as long as one promise takes, whichever way it settles. */
export function waitFor<T>(work: Promise<T>, label: string): Promise<T> {
  const done = beginWait(label);
  return work.finally(done);
}

/** GlobalWait calls this once it has hydrated. The first paint was the wait; now the page owns it. */
export function endBootWait(): void {
  release(BOOT_ID);
}

/* ── navigation ─────────────────────────────────────────────────────────── */

/** What the wait says on the way to each page. Unknown paths get the plain line. */
function navLabel(pathname: string): string {
  if (pathname === '/') return 'Opening the translator';
  if (pathname.startsWith('/login')) return 'Taking you to sign in';
  if (pathname.startsWith('/register')) return 'Opening sign-up';
  if (pathname.startsWith('/welcome')) return 'One quick question first';
  if (pathname.startsWith('/admin') || pathname.startsWith('/qwer')) return 'Opening the console';
  if (pathname.startsWith('/talent')) return 'Opening the talent card';
  if (pathname.startsWith('/_team')) return 'Gathering your team';
  return 'Opening the page';
}

/**
 * A page-to-page navigation has started (called from instrumentation-client.ts).
 *
 * Only a change of PATH counts. The router also reports query-string and hash
 * updates as transitions, and those repaint the page already on screen — a
 * full-window wait over a filter changing would be the layer crying wolf.
 */
export function beginNavWait(url: string): void {
  const s = store();
  if (!s) return;
  let to: URL;
  try { to = new URL(url, window.location.href); } catch { return; }
  if (to.origin !== window.location.origin || to.pathname === window.location.pathname) return;
  // One navigation at a time is all a router does; a second start replaces the first.
  endNavWait();
  beginWait(navLabel(to.pathname), { kind: 'nav', maxMs: NAV_MAX_MS });
}

/** The pathname changed, so whatever navigation was in flight has landed. */
export function endNavWait(): void {
  const s = store();
  if (!s) return;
  s.state.holds.filter((h) => h.kind === 'nav').forEach((h) => release(h.id));
}

/**
 * A HARD navigation — `window.location.assign` — is about to replace the document.
 *
 * The router never hears about these, so the caller says so. Instant, because
 * the page is about to stop responding anyway and the grace period would only
 * be a moment of dead controls; never released, because the document that owns
 * the hold is the one being thrown away (and the next one opens on `boot`).
 */
export function beginLeaveWait(pathname: string, label?: string): void {
  beginWait(label ?? navLabel(pathname), { instant: true, kind: 'nav', maxMs: NAV_MAX_MS });
}

/* ── React ──────────────────────────────────────────────────────────────── */

function subscribe(fn: () => void): () => void {
  const s = store();
  if (!s) return () => {};
  s.listeners.add(fn);
  return () => { s.listeners.delete(fn); };
}

/** For GlobalWait only: everything currently holding the layer up. */
export function useWaitState(): WaitState {
  return useSyncExternalStore(subscribe, () => store()?.state ?? BOOT_STATE, () => BOOT_STATE);
}

/**
 * Hold the wait for as long as `active` is true.
 *
 * The hook the components use: `useWait(tree === null && !error, 'Opening your
 * notebook')`. The hold is taken in an effect and given back by that effect's
 * cleanup, so unmounting mid-wait — closing the dialog before its data arrives
 * — releases it without the component having to think about it.
 */
export function useWait(active: boolean, label: string): void {
  useEffect(() => {
    if (!active) return;
    return beginWait(label);
  }, [active, label]);
}
